import authentication from '@kdpw/msal-b2c-react'
import { InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser'
import decode from 'jwt-decode'
import { createHttpLink } from 'apollo-link-http'
import { setContext } from 'apollo-link-context'
import { InMemoryCache } from 'apollo-cache-inmemory'
import { ApolloClient } from 'apollo-client'

const authProvider = (process.env.REACT_APP_AUTH_PROVIDER || 'b2c').toLowerCase()
const isEntra = authProvider === 'entra'

const parseScopes = value => (value || '')
  .split(/[,\s]+/)
  .map(scope => scope.trim())
  .filter(Boolean)

const b2cSettings = {
  tenant: process.env.REACT_APP_TENANT_NAME,
  instance: process.env.REACT_APP_INSTANCE,
  signInPolicy: process.env.REACT_APP_SIGN_IN_POLICY,
  applicationId: process.env.REACT_APP_APP_ID,
  cacheLocation: process.env.REACT_APP_CACHE_LOCATION,
  scopes: [process.env.REACT_APP_SCOPES],
  postLogoutRedirectUri: window.location.origin,
  redirectUri: process.env.REACT_APP_REDIRECT_URI,
  validateAuthority: false,
  resetPolicy: process.env.REACT_APP_SIGN_IN_POLICY
}

const externalTenantSubdomain = process.env.REACT_APP_ENTRA_TENANT_SUBDOMAIN
const externalTenantName = process.env.REACT_APP_ENTRA_TENANT_NAME || (
  externalTenantSubdomain ? `${externalTenantSubdomain}.onmicrosoft.com` : ''
)
const entraAuthority = process.env.REACT_APP_ENTRA_AUTHORITY || (
  externalTenantSubdomain && externalTenantName
    ? `https://${externalTenantSubdomain}.ciamlogin.com/${externalTenantName}`
    : ''
)

const entraClientId = process.env.REACT_APP_ENTRA_CLIENT_ID
const entraCacheLocation = process.env.REACT_APP_ENTRA_CACHE_LOCATION || process.env.REACT_APP_CACHE_LOCATION || 'sessionStorage'
const entraRedirectUri = process.env.REACT_APP_ENTRA_REDIRECT_URI || process.env.REACT_APP_REDIRECT_URI || window.location.origin
const entraPostLogoutRedirectUri = process.env.REACT_APP_ENTRA_POST_LOGOUT_REDIRECT_URI || window.location.origin
const entraScopes = parseScopes(process.env.REACT_APP_ENTRA_SCOPES)
const entraLocale = process.env.REACT_APP_ENTRA_LOCALE || 'es-ES'
const entraKnownAuthorities = parseScopes(process.env.REACT_APP_ENTRA_KNOWN_AUTHORITIES || (
  externalTenantSubdomain ? `${externalTenantSubdomain}.ciamlogin.com` : ''
))

const entraSettings = {
  auth: {
    clientId: entraClientId,
    authority: entraAuthority,
    knownAuthorities: entraKnownAuthorities,
    redirectUri: entraRedirectUri,
    postLogoutRedirectUri: entraPostLogoutRedirectUri,
    navigateToLoginRequestUrl: false
  },
  cache: {
    cacheLocation: entraCacheLocation,
    storeAuthStateInCookie: false
  }
}

const entraLoginRequest = {
  scopes: entraScopes.length > 0 ? entraScopes : ['openid', 'profile', 'email'],
  extraQueryParameters: {
    ui_locales: entraLocale,
    mkt: entraLocale
  }
}

const entraTokenRequest = {
  ...entraLoginRequest
}

const msalInstance = new PublicClientApplication(entraSettings)
let entraActiveAccount = null
let entraCachedToken = null

const buildForgotPasswordUrl = () => {
  const tenantName = process.env.REACT_APP_TENANT_NAME || ''
  const tenantSubdomain = tenantName.substring(0, tenantName.indexOf('.'))
  return `https://${tenantSubdomain}.b2clogin.com/${tenantName}/oauth2/v2.0/authorize?p=${process.env.REACT_APP_FORGOT_PASSWORD_POLICY}&client_id=${process.env.REACT_APP_APP_ID}&nonce=defaultNonce&redirect_uri=${process.env.REACT_APP_REDIRECT_URI}&scope=openid&response_type=id_token&prompt=login`
}

const b2cConfig = () => {
  let hash = window.location.hash

  if (hash) {
    const objMessage = {}
    hash = hash.substring(1).split('&')
    hash.map(item => {
      const i = item.split('=')
      objMessage[i[0]] = i[1]
      return item
    })
    if (objMessage.error_description) {
      if (objMessage.error_description.search('AADB2C90118') >= 0) {
        window.location.href = buildForgotPasswordUrl()
      } else {
        window.location.href = window.location.origin
      }
      return null
    }
    return b2cSettings
  }

  if (window.location.search === '?forgot-password') {
    window.location.href = buildForgotPasswordUrl()
    return null
  }

  return b2cSettings
}

const resolveEntraAccount = () => {
  const currentAccount = msalInstance.getActiveAccount && msalInstance.getActiveAccount()
  if (currentAccount) return currentAccount

  const accounts = msalInstance.getAllAccounts()
  return accounts && accounts.length > 0 ? accounts[0] : null
}

const setEntraActiveAccount = account => {
  if (!account) return

  entraActiveAccount = account
  if (msalInstance.setActiveAccount) {
    msalInstance.setActiveAccount(account)
  }
}

const getEntraClaimsFromAccount = () => {
  const account = entraActiveAccount || resolveEntraAccount()
  return account && account.idTokenClaims ? account.idTokenClaims : {}
}

const getEmailFromClaims = claims => {
  if (!claims) return ''
  if (Array.isArray(claims.emails) && claims.emails.length > 0) return claims.emails[0]
  if (claims.email) return claims.email
  if (claims.preferred_username) return claims.preferred_username
  if (claims.upn) return claims.upn
  return ''
}

const acquireEntraAccessToken = async () => {
  const account = entraActiveAccount || resolveEntraAccount()
  if (!account) return null

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...entraTokenRequest,
      account
    })
    entraCachedToken = response.accessToken || response.idToken || entraCachedToken
    if (response.account) setEntraActiveAccount(response.account)
    return entraCachedToken
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect(entraTokenRequest)
      return null
    }

    throw error
  }
}

const runB2CAuthentication = callback => {
  const authConfig = b2cConfig()
  if (!authConfig) return

  authentication.initialize(authConfig)
  authentication.run(callback)
}

const runEntraAuthentication = async callback => {
  if (!entraAuthority || !entraClientId) {
    throw new Error('Missing Microsoft Entra External ID configuration. Set REACT_APP_ENTRA_AUTHORITY and REACT_APP_ENTRA_CLIENT_ID.')
  }

  const response = await msalInstance.handleRedirectPromise()
  if (response) {
    entraCachedToken = response.accessToken || response.idToken || entraCachedToken
    setEntraActiveAccount(response.account)
  } else {
    setEntraActiveAccount(resolveEntraAccount())
  }

  if (!resolveEntraAccount()) {
    await msalInstance.loginRedirect(entraLoginRequest)
    return
  }

  await acquireEntraAccessToken()
  callback()
}

const getEntraClaims = () => {
  if (entraCachedToken) return decode(entraCachedToken)
  return getEntraClaimsFromAccount()
}

const getB2CClaims = () => {
  const token = authentication.getAccessToken()
  return token ? decode(token) : {}
}

const mapUser = claims => {
  const email = getEmailFromClaims(claims)

  return {
    id: claims.oid || claims.sub || 1,
    newUser: claims.newUser,
    name: claims.name || email,
    streetAddress: claims.streetAddress,
    city: claims.city,
    given_name: claims.given_name || claims.givenName || claims.name,
    extension_Telefono: claims.extension_Telefono || claims.phone_number,
    emails: email,
  };
}

export const config = () => isEntra ? entraSettings : b2cConfig()

export const runAuthentication = callback => {
  if (isEntra) {
    return runEntraAuthentication(callback)
  }

  return runB2CAuthentication(callback)
}

export default class AAD_B2CService {
  loggedIn = () => {
    return isEntra ? !!resolveEntraAccount() : !!authentication.getAccessToken()
  }

  logout = () => {
    if (isEntra) {
      msalInstance.logoutRedirect({
        account: entraActiveAccount || resolveEntraAccount(),
        postLogoutRedirectUri: entraPostLogoutRedirectUri
      })
      return
    }

    authentication.signOut();
  }

  getToken = () => {
    return isEntra ? entraCachedToken : authentication.getAccessToken()
  }

  getClaims = () => {
    return isEntra ? getEntraClaims() : getB2CClaims()
  }

  getUser = () => {
    return mapUser(this.getClaims())
  }

  getCustomer = () => {
    return process.env.REACT_APP_CUSTOMER
  }

  getApi = async ({ method, url, body }) => {
    const headers = new Headers({
      'Ocp-Apim-Subscription-Key': process.env.REACT_APP_SUBSCRIPTION_KEY,
      'Content-Type': 'application/json'
    })
    const response = await fetch(`${process.env.REACT_APP_BACKEND}${url}`, {
      method: method || 'GET',
      headers,
      body
    })
    return await response.json()
  }

  getApiGet = async url => {
    const headers = new Headers({
      'Content-Type': 'application/json'
    })
    return await fetch(url, headers)
  }
}

const httpLink = createHttpLink({
  // uri: `${process.env.REACT_APP_BACKEND}${process.env.REACT_APP_BACKEND_PREFIX}/web/graphql`,
  uri: `${process.env.REACT_APP_BACKEND}${process.env.REACT_APP_BACKEND_PREFIX}/graphql`,
});
const authLink = setContext((_, { headers }) => {
  return {
    headers: {
      ...headers,
      // 'Ocp-Apim-Subscription-Key': process.env.REACT_APP_SUBSCRIPTION_KEY,
      // 'Access-Control-Allow-Origin' : '*',
      // 'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE'
    }
  }
})
const cache = new InMemoryCache()
export const clientApollo = new ApolloClient({
  link: authLink.concat(httpLink),
  cache,
  fetchOptions: {
    mode: 'no-cors',
  }
})
